import { Box, Grid, styled, Typography, useMediaQuery, useTheme } from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import * as Yup from "yup";
import CustomButton from "../../../components/button/custom-button.component";
import Form from "../../../components/forms/form.component";
import PaymentMethodCheckbox from "../../../components/forms/payment-method-checkbox.component";
import Loading from "../../../components/notification/backdrop-loading.component";
import { SnackbarContext } from "../../../components/notification/snackbar.context";
import HorizontalDivider from "../../../components/utils/horizontal-divider.component";
import PaddedView from "../../../components/utils/padded-view.component";
import Spacer from "../../../components/utils/spacer.component";
import FormMerchantTncCheckbox from "../../../components/forms/form-merchant-tnc-checkbox.component";

import {
  createPaymentRequest,
  getPaymentRequestDetails,
  paymentRequestSelector,
} from "../../../services/payment-request/payment-request-slice.service";
import PreviewPaymentRequestLoader from "../loader/preview-payment-request-loader.component";

const TitleTypography = styled(Typography)(({ theme }) => ({
  fontSize: theme.fonts.fontSizes.size22,
  fontWeight: "bold",
}));

const SectionTypography = styled(Typography)(({ theme }) => ({
  fontSize: theme.fonts.fontSizes.size18,
  fontWeight: "bold",
}));

const BodyTypography = styled(Typography)(({ theme }) => ({
  fontSize: theme.fonts.fontSizes.size16,
}));

const SectionContainer = styled(Grid)(({ theme }) => ({
  backgroundColor: theme.palette.colors.bg.primary,
  borderRadius: `${theme.shape.borderRadius[2]}px`,
}));

function PaymentRequestCheckoutScreen() {
  const theme = useTheme();
  const { publicId } = useParams();
  const dispatch = useDispatch();
  const { getPaymentRequestDetailsObj } = useSelector(paymentRequestSelector);
  const createSnackBar = useContext(SnackbarContext);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const formRef = useRef();
  const [isLoading, setIsLoading] = useState(false);
  const [tnc, setTnc] = useState(false);

  const validationSchema = Yup.object().shape({
    paymentMethod: Yup.string().required().label("Payment Method"),
  });

  const handleFormSubmission = (values) => {
    setIsLoading(true);
    dispatch(createPaymentRequest(values)).then(({ meta, error, payload }) => {
      setIsLoading(false);
      if (meta.requestStatus === "fulfilled") {
        window.open(payload.data.bill.remoteUrl, "_self");
      }
      if (meta.requestStatus === "rejected") {
        createSnackBar({ message: error.message, type: "error", open: true });
      }
    });
  };

  const handleSubmit = (values) => {
    handleFormSubmission(values);
  };

  useEffect(() => {
    dispatch(getPaymentRequestDetails(publicId)).then(({ meta, error }) => {
      if (meta.requestStatus === "rejected") {
        createSnackBar({ message: error.message, type: "error", open: true });
      }
    });
  }, []);

  return (
    <Box>
      <Loading isLoading={isLoading} />
      <Spacer size="l" />
      <PaddedView multiples={2}>
        <TitleTypography>Payment Request Details</TitleTypography>
        <Spacer size="l" />
        {getPaymentRequestDetailsObj.status === "succeeded" ? (
          <Form
            innerRef={formRef}
            initialValues={{
              paymentMethod: "others",
              publicId,
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            <Grid container columnSpacing={2} rowSpacing={2}>
              <Grid container item xs={isMobile ? 12 : 7} flexDirection="column" rowSpacing={2}>
                <Grid item>
                  <SectionContainer item>
                    <PaddedView multiples={isMobile ? 3 : 4}>
                      <Typography fontSize={theme.fonts.fontSizes.size14}>
                        {getPaymentRequestDetailsObj.data.item.businessName}
                      </Typography>
                      <Spacer />
                      <Grid item>
                        <TitleTypography>
                          {getPaymentRequestDetailsObj.data.item.label}
                        </TitleTypography>
                      </Grid>
                      <Spacer size="m" />
                      <Grid item>
                        <BodyTypography>
                          Amount: RM {getPaymentRequestDetailsObj.data.item.price}
                        </BodyTypography>
                      </Grid>
                    </PaddedView>
                  </SectionContainer>
                </Grid>

                <Grid item>
                  <SectionContainer item>
                    <PaddedView multiples={isMobile ? 3 : 4}>
                      <SectionTypography>Payment Method</SectionTypography>
                      <Spacer size="m" />
                      <PaymentMethodCheckbox />
                    </PaddedView>
                  </SectionContainer>
                </Grid>
              </Grid>
              <Grid item xs={isMobile ? 12 : 5}>
                <SectionContainer item>
                  <PaddedView multiples={isMobile ? 3 : 4}>
                    <TitleTypography>Payment Summary</TitleTypography>
                    <Spacer size="m" />
                    <Grid
                      item
                      display="grid"
                      flexDirection="column"
                      alignContent="space-between"
                      minHeight="220px"
                    >
                      <Grid
                        container
                        item
                        dispaly="flex"
                        justifyContent="space-between"
                        rowSpacing={1}
                      >
                        <Grid item xs={6}>
                          <BodyTypography>Subtotal</BodyTypography>
                        </Grid>
                        <Grid item xs={6} textAlign="right">
                          <BodyTypography>
                            RM {getPaymentRequestDetailsObj.data.paymentSummary.subtotal}
                          </BodyTypography>
                        </Grid>
                        <Grid item xs={6}>
                          <BodyTypography>Tax</BodyTypography>
                        </Grid>
                        <Grid item xs={6} textAlign="right">
                          <BodyTypography>
                            RM {getPaymentRequestDetailsObj.data.paymentSummary.taxPrice}
                          </BodyTypography>
                        </Grid>
                      </Grid>
                      <Grid item>
                        <HorizontalDivider spacerSize="l" />
                        <Grid
                          container
                          item
                          dispaly="flex"
                          justifyContent="space-between"
                          rowSpacing={1}
                        >
                          <SectionTypography>Total Price</SectionTypography>
                          <SectionTypography>
                            RM {getPaymentRequestDetailsObj.data.paymentSummary.totalPrice}
                          </SectionTypography>
                        </Grid>
                        <Spacer size="xl" />
                        {!!getPaymentRequestDetailsObj.data.merchant.requireTermsToAgree && (
                          <FormMerchantTncCheckbox
                            merchantSlug={getPaymentRequestDetailsObj.data.merchant.slug}
                            setTnc={setTnc}
                          />
                        )}
                        <Grid item>
                          <CustomButton
                            disabled={
                              !!getPaymentRequestDetailsObj.data.merchant.requireTermsToAgree &&
                              typeof tnc === "boolean" &&
                              !tnc
                            }
                            fontSize={theme.fonts.fontSizes.size16}
                            onClick={() => {
                              formRef.current.handleSubmit();
                            }}
                          >
                            Confirm Payment
                          </CustomButton>
                        </Grid>
                      </Grid>
                    </Grid>
                  </PaddedView>
                </SectionContainer>
              </Grid>
            </Grid>
          </Form>
        ) : (
          <PreviewPaymentRequestLoader />
        )}
      </PaddedView>
    </Box>
  );
}

export default PaymentRequestCheckoutScreen;
